import {z} from 'zod';

import {phoneState} from './phone';

export const MAX_MESSAGE_CHARS = 500;

export enum FieldErrorCode {
  Required = 'required',
  InvalidEmail = 'invalid_email',
  InvalidPhone = 'invalid_phone',
  TooLong = 'too_long',
}

export const MAX_TEXT_FIELD_CHARS = 100;

const requiredText = z.string().trim().min(1, FieldErrorCode.Required).max(MAX_TEXT_FIELD_CHARS, FieldErrorCode.TooLong);
const optionalEmail = z
  .string()
  .trim()
  .refine(v => v === '' || z.email().safeParse(v).success, FieldErrorCode.InvalidEmail);
const phone = z
  .string()
  .refine(v => phoneState(v) !== 'empty', FieldErrorCode.Required)
  .refine(v => phoneState(v) !== 'invalid', FieldErrorCode.InvalidPhone);

export const CONTACT_METHODS = ['phone_call', 'whatsapp'] as const;
export const PREFERRED_TIMES = ['first', 'second', 'third'] as const;
export const CONTACT_INQUIRY_TYPES = ['sales', 'business'] as const;

export const contactSchema = z.object({
  firstName: requiredText,
  lastName: requiredText,
  phone,
  email: optionalEmail,
  // The stable option key the UI submits; membership is validated here so the
  // server action rejects fabricated values before any backend call.
  inquiryType: z.string().min(1, FieldErrorCode.Required).refine(isContactInquiryType, FieldErrorCode.Required),
  message: z.string().trim().min(1, FieldErrorCode.Required).max(MAX_MESSAGE_CHARS, FieldErrorCode.TooLong),
});
export type ContactValues = z.infer<typeof contactSchema>;

export const registerInterestSchema = z.object({
  firstName: requiredText,
  lastName: requiredText,
  phone,
  email: optionalEmail,
  project: z.string().max(MAX_TEXT_FIELD_CHARS),
  // Hidden context (no form field) — the parent phase the visitor was viewing,
  // carried through to the lead.
  phase: z.string().max(MAX_TEXT_FIELD_CHARS),
  // Backed by the form's "Unit Type" <Select>; the backend names it designType.
  designType: z.string().max(MAX_TEXT_FIELD_CHARS),
  // The stable option keys the UI submits; membership is validated here so the
  // server action rejects fabricated values before any backend call.
  contactMethod: z.string().min(1, FieldErrorCode.Required).refine(isContactMethod, FieldErrorCode.Required),
  preferredTime: z.string().min(1, FieldErrorCode.Required).refine(isPreferredTime, FieldErrorCode.Required),
});
export type RegisterInterestValues = z.infer<typeof registerInterestSchema>;

function isContactMethod(value: string): boolean {
  return (CONTACT_METHODS as readonly string[]).includes(value);
}
function isPreferredTime(value: string): boolean {
  return (PREFERRED_TIMES as readonly string[]).includes(value);
}
function isContactInquiryType(value: string): boolean {
  return (CONTACT_INQUIRY_TYPES as readonly string[]).includes(value);
}
